import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  Paper,
  useMediaQuery
} from '@mui/material';
import {
  EmojiEvents,
  CheckCircle,
  Cancel,
  ArrowForward,
  AutorenewOutlined,
  Block,
  ExpandMore
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import featuredService from '../../services/featuredService';
import usePaymentConfig from '../../hooks/usePaymentConfig';
import BarionLogoStrip from '../../components/Barion/BarionLogoStrip';
import BarionDataTransferNotice from '../../components/Barion/BarionDataTransferNotice';
import DialogHeader from '../../components/Common/DialogHeader';
import { PLANS, FEATURES, listPriceFor } from '../../components/Reszletek/PricingSection';

// Kiemelés vásárlása / kezelése. A csomagnevek és előnyök a /reszletek árazásával közösek
// (components/Reszletek/PricingSection: PLANS, FEATURES, listPriceFor).

const DAY_MS = 1000 * 60 * 60 * 24;
const colors = { dark: '#1b331f', green: '#2a5d32', muted: '#5b6e5d', gold: '#edbe5f', goldDark: '#c9932a' };
const formatFt = (n) => `${Math.round(n || 0).toLocaleString('hu-HU')} Ft`;

// A publikus árazással egyezően havi és éves időszak (éves = 10 havi díj)
const PURCHASE_PERIODS = [
  { key: 'MONTHLY', label: 'Havi', note: 'havonta megújítható' },
  { key: 'ANNUAL', label: 'Éves', note: '12 hónap egyben' },
];

// Tömör, jól olvasható gombok (a korábbi színátmenetes, fehér feliratú gomb olvashatatlan volt)
const darkButtonSx = { bgcolor: colors.dark, color: '#fff', '&:hover': { bgcolor: colors.green } };
const goldButtonSx = { bgcolor: colors.gold, color: colors.dark, '&:hover': { bgcolor: '#e3ae43' } };

const FAQ = [
  {
    q: 'Hogyan működik a kiemelés?',
    a: 'A kiemelt szolgáltatók a keresési találatokban az ingyenes profilok előtt jelennek meg: legelöl a Kiemelés prémium, utána a Kiemelés, majd az Előfizetés csomag. A Kiemelés prémium kategóriánként csak 3 szolgáltatónak érhető el, és a főoldalon is helyet kap.',
  },
  {
    q: 'Fizetek jutalékot a kiemelés mellett?',
    a: 'Nem. Amíg a kiemelésed aktív, a foglalásaid után nem számolunk fel jutalékot — a jutalék csak az ingyenes csomagban van.',
  },
  {
    q: 'Lemondhatom a kiemelést?',
    a: 'Igen, bármikor. A lemondás után a csomagod a jelenlegi időszak végéig aktív marad, de nem újul meg automatikusan.',
  },
  {
    q: 'Mi történik, ha elfogynak a helyek?',
    a: 'A Kiemelés prémium csomagból kategóriánként 3 hely van. Ha mind foglalt, addig nem vásárolható meg, amíg valamelyik hely fel nem szabadul. A másik két csomagnál nincs helykorlát.',
  },
  {
    q: 'Van kedvezmény hosszabb időszakra?',
    a: 'Igen: éves fizetésnél 2 hónap ingyenes, vagyis az éves díj a havi díj tízszerese.',
  },
];

const ProviderFeaturedPage = () => {
  const navigate = useNavigate();
  // md alatt egy oszlop: ott nem kell a kártyák magasságát igazítani
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [tierLimits, setTierLimits] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);

  // Purchase Dialog State
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('MONTHLY');
  const [purchasing, setPurchasing] = useState(false);
  // Automatikus megújítás: kifejezett hozzájárulás kell, ezért alapból kikapcsolt
  const [autoRenewChoice, setAutoRenewChoice] = useState(false);
  // A Partneri ÁSZF elfogadása a vásárlás feltétele (Barion-elfogadóhely követelmény)
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { featuredPaymentEnabled } = usePaymentConfig();
  const [billingProfileMissing, setBillingProfileMissing] = useState(false);
  const [renewing, setRenewing] = useState(false);

  // Cancel Dialog State
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadFeaturedData();
  }, []);

  const loadFeaturedData = async () => {
    if (!user?.provider?.categoryId) {
      setError('Nincs kategória beállítva a profilodhoz.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [tiersResponse, activeSubResponse, historyResponse] = await Promise.all([
        featuredService.getCategoryTiers(user.provider.categoryId),
        featuredService.getMyActiveSubscription(),
        featuredService.getMySubscriptions()
      ]);

      if (tiersResponse.success) {
        setTierLimits(tiersResponse.data);
      }

      if (activeSubResponse.success && activeSubResponse.data) {
        setActiveSubscription(activeSubResponse.data);
      }

      if (historyResponse.success) {
        setSubscriptionHistory(historyResponse.data);
      }
    } catch (err) {
      console.error('Error loading featured data:', err);
      setError('Hiba az adatok betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseClick = (tier) => {
    setSelectedTier(tier);
    setSelectedBillingPeriod('MONTHLY');
    setAutoRenewChoice(false);
    setTermsAccepted(false);
    setPurchaseDialogOpen(true);
  };

const handlePurchaseConfirm = async () => {
  if (!selectedTier || !user?.provider?.categoryId) return;

  try {
    setPurchasing(true);
    setError(null);

    const response = await featuredService.purchaseSubscription(
      user.provider.categoryId,
      selectedTier.tier,
      selectedBillingPeriod,
      autoRenewChoice,
      termsAccepted
    );

    if (response.success) {
      console.log('✅ Purchase response:', response.data);

      // MÓDOSÍTVA: Barion átirányítás kezelése
      if (response.data.paymentUrl) {
        setSuccess('Előfizetés létrehozva! Átirányítás a fizetési oldalra...');
        
        setTimeout(() => {
          window.location.href = response.data.paymentUrl; // Barion GatewayUrl
        }, 1500);
      } else {
        // Ha nincs Barion URL (pl. teszt környezetben)
        setSuccess('Előfizetés sikeresen létrehozva!');
        setPurchaseDialogOpen(false);
        loadFeaturedData();
      }
    } else {
      setError(response.message || 'Előfizetés létrehozása sikertelen.');
    }
  } catch (err) {
    console.error('❌ Purchase error:', err);
    if (err.data?.code === 'BILLING_PROFILE_REQUIRED') {
      setBillingProfileMissing(true);
      setPurchaseDialogOpen(false);
    }
    setError(err.data?.message || err.message || 'Hiba történt a vásárlás során.');
  } finally {
    setPurchasing(false);
  }
};

  const handleToggleAutoRenew = async () => {
    if (!activeSubscription) return;

    try {
      setError(null);
      const response = await featuredService.toggleAutoRenew(activeSubscription.id);

      if (response.success) {
        setSuccess(`Automatikus megújítás ${response.data.autoRenew ? 'bekapcsolva' : 'kikapcsolva'}.`);
        loadFeaturedData();
      }
    } catch (err) {
      setError('Automatikus megújítás módosítása sikertelen.');
    }
  };

  const handleRenew = async () => {
    if (!activeSubscription) return;

    try {
      setRenewing(true);
      setError(null);
      const response = await featuredService.renewSubscription(activeSubscription.id);
      if (response.success && response.data?.paymentUrl) {
        window.location.href = response.data.paymentUrl;
        return;
      }
      setError(response.message || 'A megújítás indítása sikertelen.');
    } catch (err) {
      if (err.data?.code === 'BILLING_PROFILE_REQUIRED') setBillingProfileMissing(true);
      setError(err.data?.message || err.message || 'A megújítás indítása sikertelen.');
    } finally {
      setRenewing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!activeSubscription) return;

    try {
      setCancelling(true);
      setError(null);

      const response = await featuredService.cancelSubscription(activeSubscription.id);

      if (response.success) {
        setSuccess('Előfizetés sikeresen lemondva.');
        setCancelDialogOpen(false);
        loadFeaturedData();
      }
    } catch (err) {
      setError('Előfizetés lemondása sikertelen.');
    } finally {
      setCancelling(false);
    }
  };

  // Időszak szerinti ár: éves = annualPrice (havi × 10, "2 hónap ingyen"); féléves = havi × 6
  // (az árlista nem ad féléves kedvezményt)
  const periodPrice = (tier, period) => {
    if (!tier) return 0;
    if (period === 'ANNUAL') return tier.annualPrice || tier.monthlyPrice * 12;
    if (period === 'SEMI_ANNUAL') return tier.semiAnnualPrice || tier.monthlyPrice * 6;
    return tier.monthlyPrice;
  };
  const periodSavings = (tier, period) => {
    const months = period === 'ANNUAL' ? 12 : period === 'SEMI_ANNUAL' ? 6 : 1;
    return Math.max(0, tier ? tier.monthlyPrice * months - periodPrice(tier, period) : 0);
  };
  const calculatePrice = () => periodPrice(selectedTier, selectedBillingPeriod);
  const calculateSavings = () => periodSavings(selectedTier, selectedBillingPeriod);

  const planOf = (tierKey) => PLANS.find(p => p.key === tierKey);
  const tierLabel = (tierKey) => planOf(tierKey)?.name || tierKey;

  // Csomagok az árazási oldallal azonos sorrendben (olcsótól a prémiumig)
  const orderedTiers = ['TIER3', 'TIER2', 'TOP']
    .map(key => tierLimits.find(t => t.tier === key))
    .filter(Boolean);

  // Csak a fizetős előnyök (amiben a csomag többet ad az ingyenesnél), a keresési helyezés elöl;
  // az ingyenes alapfunkciókat egy összefoglaló sor jelzi
  const featureRows = (tierKey) => {
    const rows = FEATURES
      .map(f => {
        if (f.key === 'commission') return { label: 'Nincs jutalék a foglalások után', order: 1 };
        const value = f.values[tierKey];
        if (value === false || value === f.values.FREE) return null;
        if (typeof value === 'string') return { label: `Keresési helyezés: ${value.toLowerCase()}`, order: 0 };
        return { label: f.label, order: 2 };
      })
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
    return [...rows, { label: 'Minden, ami az ingyenes profilban', order: 3 }];
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Betöltés...
        </Typography>
      </Container>
    );
  }

  const daysLeft = activeSubscription
    ? Math.max(0, Math.ceil((new Date(activeSubscription.endsAt) - new Date()) / DAY_MS))
    : 0;
  const totalDays = activeSubscription?.startsAt
    ? Math.max(1, Math.round((new Date(activeSubscription.endsAt) - new Date(activeSubscription.startsAt)) / DAY_MS))
    : 30;

  return (
    <Box sx={{ bgcolor: '#f7f4ec', minHeight: '100%' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        {/* Fejléc */}
        <Box sx={{ mb: { xs: 4, sm: 5 }, textAlign: 'center' }}>
          <Typography variant="overline" sx={{ color: colors.green, letterSpacing: '0.2em', fontWeight: 700 }}>
            Kiemelés
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color: colors.dark, fontSize: { xs: '1.9rem', sm: '2.6rem' }, lineHeight: 1.2 }}>
            Legyél előrébb a jegyespároknál
          </Typography>
          <Typography variant="body1" sx={{ color: colors.muted, mt: 1.5, maxWidth: 560, mx: 'auto' }}>
            Mindhárom csomaggal előrébb kerülsz a keresésben, részletes statisztikákat kapsz,
            és a foglalásaid után nem fizetsz jutalékot.
          </Typography>
        </Box>

        {!featuredPaymentEnabled && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Az online fizetés hamarosan indul — addig a csomagok nem vásárolhatók. Értesítünk, amint elérhetővé válnak.
          </Alert>
        )}

        {/* Üzenetek */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => { setError(null); setBillingProfileMissing(false); }}
            action={billingProfileMissing && (
              <Button color="inherit" size="small" onClick={() => navigate('/beallitasok?tab=3')}>
                Számlázási adatok
              </Button>
            )}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Aktív kiemelés */}
        {activeSubscription && (
          <Paper
            variant="outlined"
            sx={{ mb: { xs: 4, sm: 5 }, p: { xs: 2.5, sm: 3 }, borderRadius: 3, borderColor: colors.gold, borderWidth: 2, bgcolor: '#fffaf0' }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <EmojiEvents sx={{ fontSize: 36, color: colors.goldDark }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.dark }}>Aktív kiemelésed</Typography>
                  <Chip label={tierLabel(activeSubscription.tier)} sx={{ bgcolor: colors.dark, color: '#fff', fontWeight: 700 }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Lejárat</Typography>
                    <Typography variant="h6" sx={{ color: colors.dark }}>
                      {new Date(activeSubscription.endsAt).toLocaleDateString('hu-HU')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">({daysLeft} nap múlva)</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Automatikus megújítás</Typography>
                    <FormControlLabel
                      control={<Switch checked={activeSubscription.autoRenew} onChange={handleToggleAutoRenew} color="success" />}
                      label={activeSubscription.autoRenew ? 'Bekapcsolva' : 'Kikapcsolva'}
                    />
                    {activeSubscription.autoRenew && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {activeSubscription.hasSavedCard
                          ? 'A lejárat előtt 3 nappal automatikusan terheljük a mentett kártyádat.'
                          : 'Nincs mentett kártyád: a megújítást kézzel kell kifizetned — ekkor a kártyát is elmentjük a következő megújításhoz.'}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {activeSubscription.renewal?.status === 'SCHEDULED' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Megújítva: {new Date(activeSubscription.renewal.startsAt).toLocaleDateString('hu-HU')} – {new Date(activeSubscription.renewal.endsAt).toLocaleDateString('hu-HU')}
                  </Alert>
                )}
                {activeSubscription.renewal?.status === 'PENDING' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    A megújítás fizetésre vár ({Number(activeSubscription.renewal.paidAmount).toLocaleString('hu-HU')} Ft). Ha a lejáratig nem érkezik meg, a profilod visszakerül az ingyenes csomagba.
                  </Alert>
                )}

                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (daysLeft / totalDays) * 100)}
                  sx={{
                    mt: 2,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(colors.dark, 0.1),
                    '& .MuiLinearProgress-bar': { bgcolor: colors.goldDark, borderRadius: 4 },
                  }}
                />
              </Box>

              <Stack spacing={1}>
                {featuredPaymentEnabled && activeSubscription.renewal?.status !== 'SCHEDULED' && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={renewing ? <CircularProgress size={20} color="inherit" /> : <AutorenewOutlined />}
                    onClick={handleRenew}
                    disabled={renewing}
                    sx={darkButtonSx}
                  >
                    {activeSubscription.renewal?.status === 'PENDING' ? 'Megújítás kifizetése' : 'Meghosszabbítás most'}
                  </Button>
                )}
                <Button fullWidth variant="outlined" color="error" startIcon={<Block />} onClick={() => setCancelDialogOpen(true)}>
                  Lemondás
                </Button>
                <Button fullWidth variant="text" onClick={() => navigate('/dashboard')} sx={{ color: colors.green }}>
                  Vissza a Dashboard-hoz
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {/* Csomagok */}
        <Typography variant="h5" align="center" sx={{ fontWeight: 700, color: colors.dark, mb: 3 }}>
          {activeSubscription ? 'Csomag váltása' : 'Válassz csomagot'}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.max(orderedTiers.length, 1)}, minmax(0, 1fr))` },
            gap: { xs: 2.5, md: 3 },
            maxWidth: { xs: 440, md: 1080 },
            mx: 'auto',
            alignItems: 'stretch',
          }}
        >
          {orderedTiers.map((tier) => {
            const plan = planOf(tier.tier);
            const highlight = !!plan?.highlight;
            const isActive = activeSubscription?.tier === tier.tier;
            const isSoldOut = tier.availableSlots !== undefined ? tier.availableSlots <= 0 : tier.activeCount >= tier.maxSlots;
            const annual = periodPrice(tier, 'ANNUAL');

            return (
              <Card
                key={tier.tier}
                variant="outlined"
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  // A keret mindenhol 1px (így a tartalom egy magasságban kezdődik); a kiemelt kártyák
                  // vastagabb keretét egy belső árnyék adja
                  borderWidth: 1,
                  borderColor: isActive ? colors.green : highlight ? colors.gold : 'rgba(42,93,50,0.18)',
                  bgcolor: highlight ? '#fffaf0' : '#fff',
                  boxShadow: [
                    (highlight || isActive) && `inset 0 0 0 1px ${isActive ? colors.green : colors.gold}`,
                    highlight ? '0 10px 30px rgba(237,190,95,0.25)' : '0 4px 16px rgba(27,51,31,0.06)',
                  ].filter(Boolean).join(', '),
                  overflow: 'visible',
                }}
              >
                {(isActive || highlight) && (
                  <Chip
                    size="small"
                    label={isActive ? 'Jelenlegi csomag' : 'Legjobb láthatóság'}
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 700,
                      bgcolor: isActive ? colors.green : colors.gold,
                      color: isActive ? '#fff' : colors.dark,
                    }}
                  />
                )}

                <CardContent sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: highlight ? colors.goldDark : colors.dark }}>
                      {plan?.name || tier.tier}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.muted }}>{plan?.tagline}</Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: colors.muted, textDecoration: 'line-through' }}>
                      {formatFt(listPriceFor(tier.monthlyPrice))}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 30, sm: 34 }, fontWeight: 800, color: colors.dark, lineHeight: 1.15 }}>
                      {formatFt(tier.monthlyPrice)}
                      <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, color: colors.muted }}> / hó</Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.muted, mt: 0.5 }}>
                      Éves fizetéssel {formatFt(annual)} / év
                      {periodSavings(tier, 'ANNUAL') > 0 && (
                        <Box component="span" sx={{ color: colors.green, fontWeight: 600 }}> — 2 hónap ingyen</Box>
                      )}
                    </Typography>
                    {/* A helyszám-chip csak a TOP-nál látszik, de mindegyik kártyán foglalja a helyét,
                        hogy az elválasztó vonalak egy magasságban legyenek */}
                    {(tier.tier === 'TOP' || !isMobile) && (
                      <Chip
                        size="small"
                        aria-hidden={tier.tier !== 'TOP'}
                        label={isSoldOut ? 'Minden hely foglalt' : `Még ${tier.availableSlots ?? (tier.maxSlots - tier.activeCount)} / ${tier.maxSlots} szabad hely`}
                        sx={{
                          mt: 1.5,
                          visibility: tier.tier === 'TOP' ? 'visible' : 'hidden',
                          fontWeight: 700,
                          bgcolor: isSoldOut ? 'rgba(0,0,0,0.06)' : 'rgba(42,93,50,0.1)',
                          color: isSoldOut ? colors.muted : colors.green,
                        }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2.5, flex: 1 }}>
                    {featureRows(tier.tier).map(row => (
                      <Box component="li" key={row.label} sx={{ display: 'flex', gap: 1, py: 0.6, alignItems: 'flex-start' }}>
                        <CheckCircle sx={{ fontSize: 20, color: colors.green, mt: '1px' }} />
                        <Typography variant="body2" sx={{ color: colors.dark }}>{row.label}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    fullWidth
                    size="large"
                    variant={isActive ? 'outlined' : 'contained'}
                    disabled={isSoldOut || isActive || !featuredPaymentEnabled}
                    startIcon={isSoldOut ? <Block /> : <EmojiEvents />}
                    onClick={() => handlePurchaseClick(tier)}
                    sx={{
                      py: 1.4,
                      fontWeight: 700,
                      ...(!isActive && !isSoldOut && (highlight ? goldButtonSx : darkButtonSx)),
                    }}
                  >
                    {isSoldOut ? 'Elfogyott' : isActive ? 'Jelenlegi csomag' : !featuredPaymentEnabled ? 'Hamarosan' : 'Ezt választom'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Typography variant="caption" align="center" display="block" sx={{ color: colors.muted, mt: 2.5 }}>
          Az árak bruttó összegek, és az első 500 csatlakozó szolgáltatóra vonatkoznak.{' '}
          <Box component={RouterLink} to="/reszletek#arazas" sx={{ color: colors.green, fontWeight: 600 }}>
            Részletes összehasonlítás az ingyenes csomaggal
          </Box>
        </Typography>

        {/* GYIK */}
        <Box sx={{ mt: { xs: 6, sm: 8 }, maxWidth: 820, mx: 'auto' }}>
          <Typography variant="h5" align="center" sx={{ fontWeight: 700, color: colors.dark, mb: 3 }}>
            Gyakran ismételt kérdések
          </Typography>
          {FAQ.map(item => (
            <Accordion
              key={item.q}
              disableGutters
              elevation={0}
              sx={{ border: '1px solid rgba(42,93,50,0.15)', borderRadius: 2, mb: 1.5, '&:before': { display: 'none' }, bgcolor: '#fff' }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600, color: colors.dark }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: colors.muted }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      {/* Vásárlás */}
      <Dialog
        open={purchaseDialogOpen}
        onClose={() => !purchasing && setPurchaseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', background: 'rgba(245,241,230,0.97)' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<EmojiEvents />}
            title={selectedTier ? `${tierLabel(selectedTier.tier)} vásárlása` : 'Csomag vásárlása'}
            subtitle={user?.provider?.businessName}
            onClose={purchasing ? undefined : () => setPurchaseDialogOpen(false)}
          />
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '20px !important', pb: 1 }}>
          {selectedTier && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.dark, mb: 1 }}>
                Időszak
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2.5 }}>
                {PURCHASE_PERIODS.map(({ key, label, note }) => {
                  const selected = selectedBillingPeriod === key;
                  const savings = periodSavings(selectedTier, key);
                  return (
                    <Box
                      key={key}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => setSelectedBillingPeriod(key)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBillingPeriod(key); } }}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: '14px',
                        p: 2,
                        bgcolor: '#fff',
                        border: '2px solid',
                        borderColor: selected ? colors.green : 'rgba(27,51,31,0.12)',
                        boxShadow: selected ? '0 4px 14px rgba(42,93,50,0.15)' : 'none',
                        transition: 'border-color .15s, box-shadow .15s',
                        '&:hover': { borderColor: selected ? colors.green : 'rgba(27,51,31,0.3)' },
                        '&:focus-visible': { outline: `2px solid ${colors.gold}`, outlineOffset: 2 },
                      }}
                    >
                      {selected && (
                        <CheckCircle sx={{ position: 'absolute', top: 10, right: 10, fontSize: 20, color: colors.green }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.muted }}>{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.dark, lineHeight: 1.3 }}>
                        {formatFt(periodPrice(selectedTier, key))}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.muted }}>{note}</Typography>
                      {savings > 0 && (
                        <Chip
                          size="small"
                          label={`2 hónap ingyen · ${formatFt(savings)} megtakarítás`}
                          sx={{ mt: 1, height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(colors.gold, 0.25), color: colors.dark }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2, px: 0.5 }}>
                <Typography variant="body2" sx={{ color: colors.muted }}>Fizetendő (bruttó)</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: colors.dark }}>{formatFt(calculatePrice())}</Typography>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <FormControlLabel
                sx={{ alignItems: 'flex-start', mb: 1, mx: 0 }}
                control={<Checkbox checked={autoRenewChoice} onChange={(e) => setAutoRenewChoice(e.target.checked)} sx={{ pt: 0.25 }} />}
                label={
                  <Typography variant="body2">
                    <strong>Automatikus megújítás</strong> — hozzájárulok, hogy a Barion elmentse a bankkártyámat, és az
                    időszak lejárta előtt 3 nappal a csomag díját ({formatFt(calculatePrice())}) automatikusan terheljék.
                    Bármikor kikapcsolható.
                  </Typography>
                }
              />
              <FormControlLabel
                sx={{ alignItems: 'flex-start', mb: 1.5, mx: 0 }}
                control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} sx={{ pt: 0.25 }} />}
                label={
                  <Typography variant="body2">
                    Elolvastam és elfogadom a{' '}
                    <RouterLink to="/partneri-aszf" target="_blank" rel="noopener noreferrer">Partneri ÁSZF</RouterLink>-et
                    és az{' '}
                    <RouterLink to="/adatvedelmi-nyilatkozat" target="_blank" rel="noopener noreferrer">Adatkezelési tájékoztató</RouterLink>t. *
                  </Typography>
                }
              />

              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', p: 1.5, mb: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', color: colors.dark, fontWeight: 600, mb: 0.5 }}>
                  Azonnali aktiválás a sikeres fizetés után · a számlát e-mailben küldjük · bármikor lemondható
                </Typography>
                <BarionDataTransferNotice />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider', flexWrap: 'wrap' }}>
          <BarionLogoStrip variant="light" height={{ xs: 28, sm: 34 }} sx={{ mr: 'auto' }} />
          <Button onClick={() => setPurchaseDialogOpen(false)} disabled={purchasing} sx={{ color: colors.muted }}>
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={handlePurchaseConfirm}
            disabled={purchasing || !termsAccepted}
            startIcon={purchasing ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
            sx={{ ...darkButtonSx, borderRadius: '10px', px: 3, fontWeight: 700 }}
          >
            {purchasing ? 'Folyamatban...' : 'Tovább a fizetéshez'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lemondás */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kiemelés lemondása</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Biztosan le szeretnéd mondani a kiemelésedet?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            A lemondás után a kiemelésed a jelenlegi időszak végéig ({activeSubscription && new Date(activeSubscription.endsAt).toLocaleDateString('hu-HU')})
            aktív marad, de nem újul meg automatikusan.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A kiemelés lejárta után a profilod visszakerül az ingyenes csomagba.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Mégsem
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} color="inherit" /> : <Cancel />}
          >
            {cancelling ? 'Lemondás...' : 'Lemondás megerősítése'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderFeaturedPage;
